import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { getAPIHandler, rawAPICall } from "src/Apiconfig/service";
import { v4 as uuidv4 } from "uuid";
import { calculateTimeLeft } from "src/utils";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth, useHandleLogin } from "src/firebase";
import ApiConfig from "src/Apiconfig/config";
import { jwtDecode } from "jwt-decode";

let globalLastProcessedUid = null;
// Shared across all concurrent 401s so a burst of failing requests triggers
// exactly one Firebase token refresh instead of one per request.
let tokenRefreshPromise = null;
let isRedirectingToLogin = false;
const getRefreshedToken = (user) => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = user.getIdToken(true).finally(() => {
      tokenRefreshPromise = null;
    });
  }
  return tokenRefreshPromise;
};
const isTokenExpired = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    const now = Date.now() / 1000;
    return exp < now;
  } catch (err) {
    console.error("Invalid token", err);
    return true;
  }
};

export const AuthContext = createContext();

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("token", accessToken);
    axios.defaults.headers.common.Authorization = `${accessToken}`;
  } else {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common.Authorization;
  }
};

function checkLogin() {
  const accessToken = window.localStorage.getItem("accessToken");
  return accessToken ? true : false;
}

export default function AuthProvider(props) {
  const UUID = uuidv4();
  const [endTime, setEndtime] = useState();
  const source = axios.CancelToken.source();
  const [isLogin, setIsLogin] = useState(checkLogin());
  const [userAddedWallets, setUserAddedWallets] = useState([]);
  const [timeLeft, setTimeLeft] = useState();
  const [userData, setUserData] = useState({});
  const [disableHeaders, setDisableHeaders] = useState(false);
  const [walletConfigCache, setWalletConfigCache] = useState([]);
  const [userProfileInfo, setUserProfileInfo] = useState({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [walletApiStatus, setWalletApiStatus] = useState(null);
  useHandleLogin();

  useEffect(() => {
    const savedProfile = localStorage.getItem("userProfileInfo");
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        console.log("🔄 [AuthContext] Restored profile from localStorage:", parsed);
        setUserProfileInfo(parsed);
      } catch (err) {
        console.error("❌ Error parsing saved profile:", err);
        localStorage.removeItem("userProfileInfo");
      }
    }
    setIsLoadingProfile(false);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      
      // Check for signup flag to prevent unnecessary API calls during signup flow
      const isSignupInProgress = sessionStorage.getItem('signup_in_progress');

      if (user && !isSignupInProgress) { 

        if (globalLastProcessedUid === user.uid) {
            console.log("[AuthContext] Skipping duplicate auth event for:", user.uid);
            return;
        }

        globalLastProcessedUid = user.uid;
        console.log("[AuthContext] User authenticated:", user.uid);
    
        try {
          const token = await user.getIdToken();
          localStorage.setItem("accessToken", token);
          axios.defaults.headers.common.Authorization = token;
          
          setUserData(user);
          setIsLogin(true);
          
          // ✅ INTERCOM: Merge the visitor into an authenticated user 
          if (window.Intercom) {
            window.Intercom('update', {
              app_id: 'mcbsohcq',
              user_id: user.uid,
              email: user.email,
              created_at: user.metadata?.creationTime ? Math.floor(new Date(user.metadata.creationTime).getTime() / 1000) : undefined,
              // user_hash: '...' // TODO: You will need to implement HMAC hashing from your Java backend here eventually
            });
          }
          
          await Promise.all([
            fetchUserProfile(user.uid, token),
            getAllAddedWallets(source, user.uid, token),
            getWalletCofigurationList(source, token)
          ]);
          
          console.log("✅ [AuthContext] Authentication and data loading complete");
        } catch (error) {
          console.error("❌ [AuthContext] Error during authentication:", error);
          setIsLogin(false);
        }
      } else {
        console.log("[AuthContext] User logged out");
        globalLastProcessedUid = null;
        setUserData({});
        setIsLogin(false);
        updateUserProfileInfo({});
        setUserAddedWallets([]);
        setWalletConfigCache([]);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userProfileInfo");
        
        sessionStorage.removeItem('signup_in_progress'); 

        // ✅ INTERCOM: Wipe the session on logout and restart as a fresh visitor
        if (window.Intercom) {
          window.Intercom('shutdown');
          window.Intercom('boot', { app_id: 'mcbsohcq' }); 
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRedirectingToLogin) {
            // Already committed to bouncing to login; don't pile on more
            // refresh attempts for every other request failing in the meantime.
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const user = firebaseAuth.currentUser;
            if (user) {
              const refreshedToken = await getRefreshedToken(user);
              window.localStorage.setItem("accessToken", refreshedToken);
              axios.defaults.headers.common.Authorization = refreshedToken;
              originalRequest.headers.Authorization = refreshedToken;
              return axios(originalRequest);
            } else {
              throw new Error("No user logged in");
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            if (!isRedirectingToLogin) {
              isRedirectingToLogin = true;
              window.localStorage.removeItem("accessToken");
              setIsLogin(false);
              window.location.href = "/secure/login?reason=session_expired";
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const getAllAddedWallets = async (source, userId = userData?.uid, token = null) => {
    try {
      const accessToken = token || window.localStorage.getItem("accessToken");
      const uid = userId || userData?.uid;
      
      const response = await getAPIHandler({
        endPoint: "wallet",
        source: source,
        token: accessToken,
      });

      setWalletApiStatus(response?.status);

      if (response?.status === 200 && response.data.length > 0) {
        setUserAddedWallets(response.data);
        setDisableHeaders(false);
        console.log("✅ [AuthContext] Wallets loaded:", response.data.length);
      } else {
        setUserAddedWallets([]);
        setDisableHeaders(true);
      }
    } catch (error) {
      console.log("❌ [AuthContext] Error fetching wallets:", error);
      setWalletApiStatus(error?.response?.status);
      setUserAddedWallets([]);
      setDisableHeaders(true);
    }
  };

  // ✅ FIXED: Single function to update profile info (both state and localStorage)
  const updateUserProfileInfo = (profileInfoOrUpdater) => {
    const profileInfo = typeof profileInfoOrUpdater === 'function'
      ? profileInfoOrUpdater(userProfileInfo)
      : profileInfoOrUpdater;

    if (profileInfo && Object.keys(profileInfo).length > 0) {
      const newProfileInfo = { ...profileInfo };
      setUserProfileInfo(newProfileInfo);
      localStorage.setItem("userProfileInfo", JSON.stringify(newProfileInfo));
      console.log("✅ [AuthContext] Profile info updated:", newProfileInfo);
    } else {
      setUserProfileInfo({});
      localStorage.removeItem("userProfileInfo");
      console.log("🔓 [AuthContext] Profile info cleared");
    }
  };

  const fetchUserProfile = async (userId, token) => {
    try {
      console.log("🔍 [AuthContext] Fetching user profile...");
      
      const accessToken = token || localStorage.getItem("accessToken");
      const prefUrl = `${ApiConfig["userProfile"]}`;

      const prefResponse = await rawAPICall({
        method: "GET",
        url: prefUrl,
        headers: { Authorization: accessToken },
      });

      if (prefResponse?.status === 200) {
        const { homeCountry, preferredCurrency, avatarName, costBasisType } = prefResponse.data;

        const countryResponse = await getAPIHandler({
          endPoint: "country",
          token: accessToken,
        });

        if (countryResponse?.status === 200 && Array.isArray(countryResponse.data)) {
          const enabledCountries = countryResponse.data.filter(item => item.enabled);
          let matchedCountry = enabledCountries.find(
            item => item.acronym === homeCountry
          );

          if (!matchedCountry) {
            matchedCountry = enabledCountries.find(
              item => item.name === homeCountry
            );
          }

          if (matchedCountry) {
            const currencyCountry = enabledCountries.find(
              item => item.currency === preferredCurrency
            );
            const finalProfile = {
              name: matchedCountry.acronym, 
              fullName: matchedCountry.name, 
              homeCountry: homeCountry, 
              currency: preferredCurrency,
              symbol: currencyCountry?.symbol ,
              costBasisType: costBasisType || "",
              isEnabled: true,
              acronym: matchedCountry.acronym,
              username: avatarName,
            };
            
            console.log("✅ [AuthContext] Profile loaded:", finalProfile);
            updateUserProfileInfo(finalProfile);

            // ✅ INTERCOM: Enhance the user profile with custom backend data
            if (window.Intercom) {
              window.Intercom('update', {
                name: finalProfile.fullName || finalProfile.username,
                home_country: finalProfile.homeCountry,
                currency: finalProfile.currency
              });
            }

          } else {
            console.warn("⚠️ [AuthContext] No matching country found for:", homeCountry);
          }
        }
      }
    } catch (prefError) {
      if (prefError.response?.status === 404) {
        console.warn("⚠️ [AuthContext] Profile not found - user needs onboarding");
        updateUserProfileInfo({});
      } else {
        console.error("❌ [AuthContext] Error fetching profile:", prefError);
      }
    }
  };

  const getWalletCofigurationList = async (source, token = null) => {
    try {
      const accessToken = token || window.localStorage.getItem("accessToken");
      
      const response = await getAPIHandler({
        endPoint: "wallet",
        id: "configuration",
        source: source,
        token: accessToken,
      });

      if (response?.status === 200 && Array.isArray(response.data)) {
        setWalletConfigCache(response.data);
      } else {
        setWalletConfigCache([]);
      }
    } catch (error) {
      console.error(" [AuthContext] Error fetching wallet configuration:", error);
      setWalletConfigCache([]);
    }
  };

  const updateUserData = (newFields) => {
    setUserData((prevData) => ({
      ...prevData,
      ...newFields,
    }));
  };

  const refreshUserProfile = async () => {
    if (userData?.uid) {
      const token = localStorage.getItem("accessToken");
      await fetchUserProfile(userData.uid, token);
    }
  };

  // NEW: Function to force refresh the Firebase token and save it globally
  const forceTokenRefresh = async () => {
    try {
      const user = firebaseAuth.currentUser;
      if (user) {
        console.log("🔄 [AuthContext] Forcing Firebase token refresh...");
        // The 'true' flag forces Firebase to bypass the cache and hit the server
        const refreshedToken = await user.getIdToken(true); 
        
        // Save the new token with the updated custom claims
        localStorage.setItem("accessToken", refreshedToken);
        axios.defaults.headers.common.Authorization = refreshedToken;
        
        console.log("✅ [AuthContext] Token refreshed and saved globally.");
        return refreshedToken;
      }
    } catch (error) {
      console.error("❌ [AuthContext] Failed to force token refresh", error);
    }
    return null;
  };

  let data = {
    UUID,
    timeLeft,
    userData,
    userAddedWallets,
    walletApiStatus,
    setUserAddedWallets,
    userLoggedIn: isLogin,
    userLogIn: (type, data) => {
      setIsLogin(type);
    },
    setUserData: (data) => setUserData(data),
    updateUserData: updateUserData,
    setEndtime: (data) => setEndtime(data),
    getAllAddedWallets: () => getAllAddedWallets(source),
    userProfileInfo,
    setUserProfileInfo: updateUserProfileInfo,
    refreshUserProfile,
    walletConfigCache,
    setWalletConfigCache,
    isLoadingProfile,
    forceTokenRefresh,
  };

  useEffect(() => {
    if (endTime) {
      const timer = setTimeout(() => {
        setTimeLeft(calculateTimeLeft(endTime * 1000));
      }, 1000);
      return () => clearTimeout(timer);
    }
  });

  return (
    <AuthContext.Provider value={data}>{props.children}</AuthContext.Provider>
  );
}